import { useEffect, useMemo, useState } from 'react'
import { FiFilter, FiPlus, FiX } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { usePaginatedActivities } from '@presentation/hooks/usePaginatedActivities'
import { useNewsCategories } from '@presentation/hooks/useNewsCategories'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import NewsCard from '@presentation/components/ui/NewsCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import SeoHead from '@presentation/seo/SeoHead'

export default function NewsListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isNewsListRoute = location.pathname === '/news'
  const screenPath = `${location.pathname}${location.search || ''}`
  const { user } = useAuthUser()
  const {
    activities,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMore,
    newsCategoryId,
    setCategoryFilter,
    searchQuery,
    setSearchQuery,
  } = usePaginatedActivities()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryFromUrl = searchParams.get('q') ?? ''
  useScrollToTopOnRouteEnter({ enabled: isNewsListRoute })

  const { categories } = useNewsCategories()
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [draftCategoryId, setDraftCategoryId] = useState<string | null>(null)

  useEffect(() => {
    if (!isNewsListRoute) {
      return
    }

    if (queryFromUrl !== searchQuery) {
      setSearchQuery(queryFromUrl)
    }
  }, [isNewsListRoute, queryFromUrl, searchQuery, setSearchQuery])

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>()
    categories.forEach((c) => map.set(c.uid, c.name))
    return map
  }, [categories])

  const updateQueryParam = (query: string) => {
    const currentValue = searchParams.get('q') ?? ''
    const nextValue = query.trim().length > 0 ? query : ''

    if (currentValue === nextValue) {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    if (nextValue) {
      nextParams.set('q', nextValue)
    } else {
      nextParams.delete('q')
    }

    setSearchParams(nextParams, { replace: true })
  }

  const handleSearchChange = (query: string) => {
    if (query.trim().length > 0) {
      AnalyticsFeature.trackSearchRun(screenPath, 'news_list_search')
    } else if (searchQuery.trim().length > 0) {
      AnalyticsFeature.trackFilterClear(screenPath, 'news_list_search')
    }
    setSearchQuery(query)
    updateQueryParam(query)
  }

  const handleCategoryFilter = (nextCategoryId: string | null) => {
    void setCategoryFilter(nextCategoryId)
  }

  const openFilterPopup = () => {
    setDraftCategoryId(newsCategoryId)
    setIsFilterOpen(true)
  }

  const closeFilterPopup = () => {
    setIsFilterOpen(false)
  }

  const applyFilterSelection = () => {
    if (draftCategoryId) {
      AnalyticsFeature.trackFilterApply(screenPath, 'news_list_filter', draftCategoryId)
    } else {
      AnalyticsFeature.trackFilterClear(screenPath, 'news_list_filter')
    }
    handleCategoryFilter(draftCategoryId)
    setIsFilterOpen(false)
  }

  const removeFilters = () => {
    AnalyticsFeature.trackFilterClear(screenPath, 'news_list_filter')
    setDraftCategoryId(null)
    handleCategoryFilter(null)
    setIsFilterOpen(false)
  }

  const navigateBackToHome = () => {
    AnalyticsFeature.trackNavClick(screenPath, '/')
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/', { replace: true })
  }

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      {isNewsListRoute ? (
        <SeoHead
          title="Parkinson's News | Parkinson's Insights"
          description="Browse recent Parkinson's articles, updates, and research coverage with category filters and keyword search."
          canonicalPath="/news"
        />
      ) : null}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={navigateBackToHome}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light md:hidden"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="mt-5 ml-1 flex items-center justify-between gap-3 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
            News
          </h1>
          <div className="flex items-center gap-2">
            {user?.isAdmin ? (
              <Link
                to="/news/create"
                onClick={() => AnalyticsFeature.trackNavClick(screenPath, '/news/create')}
                aria-label="Create news article"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
              >
                <FiPlus className="h-5 w-5" aria-hidden="true" />
              </Link>
            ) : null}
            {categories.length > 0 && (
              <button
                type="button"
                onClick={openFilterPopup}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
                  newsCategoryId
                    ? 'border-blue bg-blue text-white hover:bg-blue/85'
                    : 'border-blue text-blue hover:bg-blue/8'
                }`}
              >
                <FiFilter className="h-4 w-4" aria-hidden="true" />
                Filter
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-4">
          {/* Search */}
          <div className="relative max-w-md md:hidden">
            <svg
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search news by keywords..."
              className="w-full rounded-xl border border-border bg-surface py-3 pl-10 pr-11 text-base font-semibold text-black shadow-sm transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!"
            />
            {searchQuery.trim().length > 0 && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-text-muted transition-colors hover:text-primary"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {initialLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="mt-8 text-center text-text-secondary">
            Unable to load news. Please try again later.
          </p>
        ) : activities.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              No articles found
            </p>
            <p className="mt-1 text-sm text-text-muted">
              {searchQuery
                ? 'Try different search term or check and change your filter selection.'
                : newsCategoryId
                  ? 'No articles in this category. Try selecting a different one.'
                  : 'Check back later for new content.'}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {activities.map((activity, index) => (
                <NewsCard
                  key={activity.uid}
                  activity={activity}
                  variant="compact"
                  priority={index === 0}
                  categoryName={
                    activity.newsCategoryId
                      ? categoryMap.get(activity.newsCategoryId)
                      : undefined
                  }
                />
              ))}
            </div>

            {hasMore && !searchQuery && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => {
                    AnalyticsFeature.trackLoadMore(screenPath, 'news_list', 'news')
                    void loadMore()
                  }}
                  disabled={loading}
                  className="rounded-xl bg-primary px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-light disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isFilterOpen && (
        <div className="fixed inset-0 z-60 bg-black/55 px-4 py-8 sm:px-6">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close filters"
            onClick={closeFilterPopup}
          />

          <section
            role="dialog"
            aria-modal="true"
            aria-label="News filters"
            className="relative z-10 mx-auto flex w-full max-w-xl flex-col rounded-2xl bg-surface p-5 shadow-2xl sm:p-6"
          >
            <h3 className="text-2xl font-bold text-text-primary">Filter news</h3>
            <p className="mt-1 text-base text-text-secondary">
              Select one category and apply when ready.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDraftCategoryId(null)}
                className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                  !draftCategoryId
                    ? 'bg-blue text-white'
                    : 'bg-surface text-black shadow-sm hover:bg-border-light'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.uid}
                  type="button"
                  onClick={() =>
                    setDraftCategoryId((current) => (
                      current === cat.uid ? null : cat.uid
                    ))
                  }
                  className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                    draftCategoryId === cat.uid
                      ? 'bg-blue text-white'
                      : 'bg-surface text-black shadow-sm hover:bg-border-light'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={applyFilterSelection}
                className="w-full rounded-lg bg-violet px-5 py-2.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={closeFilterPopup}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeFilters}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Remove filters
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
